// src/belediye/components/notifications/NotificationsBell.js

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import {
  BellIcon,
  BellSlashIcon,
  CheckIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";

import {
  fetchNotifications,
  markRead,
  markAllRead,
  markOneAsReadLocal,
  // FAZ-60: zil MESAJ bildirimi taşımaz — kapsam selector'da (tek kaynak).
  // "Tümü" sayfası eski selector'ları kullanmaya devam eder.
  selectBellDropdownNotifications,
  selectBellUnreadCount,
  // FAZ-85: ikon rozeti artık SAYI değil NOKTA — tek soru: okunmamış var mı?
  selectHasUnread,
  // FAZ-77: zil mesaj taşımıyor ama okunmamış mesajı da SESSİZCE yutmuyor —
  // pencerenin altında ayrı bir çizgi olarak duruyor.
  selectMesajUnreadCount,
  selectNotificationsLoading,
  selectNotificationsError,
  selectNotificationsMarkingRead,
} from "../../redux/slices/belediyeNotificationsSlice";

// FAZ-69: Bildirim ayarları artık YALNIZ merkez sayfasında (zil bir önizlemedir).
import useModuleEnabled from "../../hooks/useModuleEnabled";
import { sourceIcon, conversationIcon } from "../icons/appIcons";
import {
  actorName,
  bildirimNotu,
  kayitEtiketi,
  sourceLabel,
} from "./notificationMeta";
import { openChat, toggleChat } from "../../services/mesajlasmaBridge";
import resolveNotificationTarget from "./resolveNotificationTarget";
import "./NotificationsBell.css";

const LS = {
  user: "user",
  activeUnitSlug: "activeUnitSlug",
};

function safeJsonParse(v) {
  try {
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

const safeStr = (v) => (v === null || v === undefined ? "" : String(v));

// FAZ-77 — ZAMAN GÖSTERİMİ: tam tarih değil, GÖRELİ zaman.
//
// Pencere 15 satırlık bir önizleme; her satırda "30.07.2026 14:23" yazmak
// başlığın yerini yiyordu ve zaten okunmuyordu — insan orada "ne zaman"ı
// "kaç dakika önce" olarak okur. Bir günden eskiler gün/ay olarak yazılır,
// tam tarih `title` özelliğinde durur (imleçle görülür, bilgi kaybı yok).
const formatRelativeTR = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return safeStr(value);

  const gecen = Date.now() - d.getTime();
  const dk = Math.floor(gecen / 60000);
  if (dk < 1) return "şimdi";
  if (dk < 60) return `${dk} dk`;

  const sa = Math.floor(dk / 60);
  if (sa < 24) return `${sa} sa`;

  const gun = Math.floor(sa / 24);
  if (gun === 1) return "dün";
  if (gun < 7) return `${gun} gün`;

  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" });
};

const formatDateTimeTR = (value) => {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return safeStr(value);
    return d.toLocaleString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return safeStr(value);
  }
};

const pickEvent = (n) => safeStr(n?.event || n?.inapp_payload?.event).trim();
// FAZ-49: cancelled artık GİZLENMİYOR (kullanıcı kararı 2026-07-26) — üretilip
// hiç gösterilmediği için DB'de okunmamış birikiyordu.
const isCancelledEvent = () => false;

const pickTitle = (n) => {
  // FAZ-33: backend artık title üretir → önce onu kullan.
  const backendTitle = safeStr(n?.title).trim();
  if (backendTitle) return backendTitle;
  const ev = pickEvent(n);
  if (ev.includes("close_request.created")) return "Yeni kapatma talebi";
  if (ev.includes("close_request.approved")) return "Kapatma talebi onaylandı";
  if (ev.includes("close_request.rejected")) return "Kapatma talebi reddedildi";
  return ev || "Bildirim";
};

// FAZ-85: gövde artık iki parça — HANGİ KAYIT (kayitEtiketi) ve NE YAZILDI
// (bildirimNotu). Zil bir önizleme olduğu için ikisi de tek satırda kırpılır,
// ama ayrı öğeler olarak: kayıt koyu, not gri.
// (FAZ-69: gövde başlıkla aynıysa hiç yazılmaz — kural notificationMeta'da.)

function readActiveUnitSlugSafe() {
  try {
    return safeStr(localStorage.getItem(LS.activeUnitSlug)).trim();
  } catch {
    return "";
  }
}


/** Satır simgesi — sayfa/toast ile aynı kural (kaynak; mesajda sohbet türü). */
function rowIcon(n) {
  const src = safeStr(n?.source_module).trim();
  if (src === "mesajlasma") {
    return conversationIcon(safeStr(n?.payload?.kind || n?.inapp_payload?.kind));
  }
  return sourceIcon(src);
}

/** Yükleniyor iskeleti — FAZ-77.
 *
 * Eskiden yükleme "Yükleniyor…" adında bir UYARI KUTUSU olarak listenin ÜSTÜNE
 * ekleniyordu: kutu her açılışta beliriyor, satırları aşağı itiyor, sonra
 * kaybolup geri çekiyordu. Tıklamak istediğiniz satır elinizin altından
 * kayıyordu. Artık iki ayrı davranış var:
 *   • liste BOŞ + yükleniyor → yerini tutan iskelet satırlar (zıplama yok)
 *   • liste DOLU + yükleniyor → başlıktaki ince şerit (yerleşim hiç oynamaz)
 */
function SkeletonList() {
  return (
    <div className="nb-notifs-list" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="nb-skel-item">
          <span className="nb-skel-ic" />
          <span className="nb-skel-lines">
            <span className="nb-skel-line nb-skel-line--title" />
            <span className="nb-skel-line nb-skel-line--msg" />
          </span>
        </div>
      ))}
    </div>
  );
}

function NotificationsList({ items, onItemClick, markingRead }) {
  const readIds = useSelector((s) => s?.belediyeNotifications?.readIds || {});
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  // FAZ-77: boş durum düz bir cümleydi ("Henüz bildirim yok."). Boş ekran da bir
  // ekrandır — simge + tek satır açıklama, sayfadaki .nx-empty diliyle aynı.
  if (!list.length) {
    return (
      <div className="nb-notifs-empty">
        <BellSlashIcon className="nb-notifs-empty-icon" />
        <div className="nb-notifs-empty-title">Bildirim yok</div>
        <div className="nb-notifs-empty-sub">Yeni bildirimler burada görünecek.</div>
      </div>
    );
  }

  return (
    <div className="nb-notifs-list">
      {list.map((n) => {
        const id = Number(n?.id);
        if (!id) return null;

        // cancelled: UI’da hiç yok
        if (isCancelledEvent(n)) return null;

        const backendIsRead = Boolean(n?.is_read);
        const localIsRead = Boolean(readIds?.[id]);
        const isRead = backendIsRead || localIsRead;
        const lvl = safeStr(n?.level) || "info";

        const title = pickTitle(n);
        const kayit = kayitEtiketi(n);
        const not_ = bildirimNotu(n);
        const kim = actorName(n);
        const ham = n?.created_at_iso || n?.created_at;
        const dt = formatRelativeTR(ham);
        const dtTam = formatDateTimeTR(ham);
        const Icon = rowIcon(n);
        const kaynak = sourceLabel(n?.source_module);

        return (
          <button
            key={id}
            type="button"
            className={[
              "nb-notifs-item",
              isRead ? "is-read" : "is-unread",
              `nb-lvl-${lvl}`,
            ].join(" ")}
            onClick={() => onItemClick(n)}
            disabled={markingRead && !isRead}
            title={`${title} · ${dtTam}`}
          >
            {/* FAZ-69: satır simgesi KAYNAĞI anlatır. Eskiden okunmamış her
                satırda kırmızı halkalı bir nokta yanıyordu — 15 satırın 15'i
                kırmızı olunca kırmızı hiçbir şey ifade etmiyordu. Okunmamışlık
                artık sol şerit + hafif zeminle veriliyor (kırmızı yalnız ACİL
                seviyeye ait). */}
            <span className="nb-notifs-item-ic">
              <Icon />
            </span>
            <span className="nb-notifs-item-left">
              <span className="nb-notifs-item-row1">
                <span className="nb-notifs-item-title">{title}</span>
                <span className="nb-notifs-item-meta">{dt}</span>
              </span>
              {/* FAZ-85 — HANGİ KAYIT kendi satırında, koyu. */}
              {kayit ? <span className="nb-notifs-item-kayit">{kayit}</span> : null}
              {not_ ? <span className="nb-notifs-item-msg">{not_}</span> : null}
              {/* FAZ-77: satır artık HANGİ KAYIT olduğunu da söylüyor.
                  Önizlemede "Talebiniz onaylandı" ile "Görev tamamlandı" aynı
                  tipografiyle yan yana durunca hangi işin bildirimi olduğu
                  yalnız ikondan anlaşılıyordu.
                  FAZ-85: kaynağın yanına KİM olduğu da eklendi. */}
              <span className="nb-notifs-item-src">
                {kim ? <b className="nb-notifs-item-who">{kim}</b> : null}
                {kim ? " · " : ""}
                {kaynak}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function NotificationsBell() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Kullanıcı kararı (2026-07-15): Bildirim Merkezi modül — kapalıyken zil İZ
  // BIRAKMAZ (uçlar zaten 404; bu, boş-zil/404 gürültüsünü de keser).
  const bildirimModulu = useModuleEnabled("bildirim");

  // user: localStorage tek sefer
  const user = useMemo(() => safeJsonParse(localStorage.getItem(LS.user)), []);
  const canRender = Boolean(user) && bildirimModulu;

  const [activeUnitSlug, setActiveUnitSlug] = useState(() => readActiveUnitSlugSafe());
  const [open, setOpen] = useState(false);

  const loading = useSelector(selectNotificationsLoading); // sadece user-initiated fetch’lerde true olmalı
  const error = useSelector(selectNotificationsError);
  const markingRead = useSelector(selectNotificationsMarkingRead);

  // Pencere içindeki "(N okunmamış)" ve "Okundu" düğmesi için SAYI gerekir;
  // ikonun üstündeki nokta için yalnız VAR/YOK (FAZ-85).
  const unreadCount = useSelector((s) => selectBellUnreadCount(s, activeUnitSlug));
  const hasUnread = useSelector(selectHasUnread);

  // dropdown list: unread-first + limit 15 (mesajlar hariç)
  const dropdownItems = useSelector((s) =>
    selectBellDropdownNotifications(s, activeUnitSlug, 15)
  );
  // FAZ-77: mesaj okunmamışı zilde SAYILMAZ ama pencerenin altında söylenir.
  const mesajUnread = useSelector(selectMesajUnreadCount);
  const mesajModulu = useModuleEnabled("mesajlasma");

  const rootRef = useRef(null);
  const lastMaxIdRef = useRef(0); // FAZ-33: since_id delta (istekler hafif kalsın)

  const DEBUG = String(process.env.REACT_APP_DEBUG_CLOSE_APPROVAL || "").trim() === "1";
  const log = useCallback(
    (...args) => {
      if (DEBUG) console.log("[NotificationsBell]", ...args);
    },
    [DEBUG]
  );

  // activeUnitSlug storage sync (multi-unit switch)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === LS.activeUnitSlug) {
        setActiveUnitSlug(readActiveUnitSlugSafe());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ---- Fetch helpers ---------------------------------------------------

  // Polling fetch:   silent => dropdown banner zıplamaz
  const fetchUnreadFast = useCallback(async () => {
    if (!canRender) return;
    try {
      const res = await dispatch(
        fetchNotifications({
          unreadOnly: true,
          limit: 80,
          mode: "merge",
          silent: true,
          sinceId: lastMaxIdRef.current || undefined, // yalnız yeni gelenler
        })
      ).unwrap?.();
      const arr = Array.isArray(res?.items) ? res.items : [];
      for (const it of arr) {
        const id = Number(it?.id) || 0;
        if (id > lastMaxIdRef.current) lastMaxIdRef.current = id;
      }
    } catch (e) {
      log("fetchUnreadFast err", e);
    }
  }, [dispatch, canRender, log]);

  // Dropdown açılınca: backend truth için replace (silent:false => isteğe bağlı loading gösterebilir)
  const fetchDropdownList = useCallback(
    async ({ silent = false } = {}) => {
      if (!canRender) return;
      try {
        await dispatch(
          fetchNotifications({
            unreadOnly: false,
            limit: 150,
            mode: "replace",
            silent,
          })
        ).unwrap?.();
      } catch (e) {
        log("fetchDropdownList err", e);
      }
    },
    [dispatch, canRender, log]
  );

  // FAZ-65 — PERİYODİK YOKLAMA KALDIRILDI (kullanıcı kararı 2026-07-28:
  // "sürekli otomatik yenilemeyi kaldıralım, çok sinir bozucu").
  //
  // Zil 5 saniyede bir istek atıyordu; ekranda çalışırken hiçbir şey
  // yapmadığınız hâlde arayüz durup durup tazeleniyordu. Artık zamanlayıcı YOK.
  // Tazeleme noktaları (hepsi kullanıcının ZATEN yaptığı bir hareket):
  //   • ilk yükleme
  //   • sekmeye/pencereye geri dönüş
  //   • sayfa değiştirme (rota) — gezinmek doğal bir tazeleme anıdır
  //   • zile tıklayınca (aşağıdaki effect) ve "Yenile" düğmesi
  // Bedeli kabul edildi: ekranda kıpırdamadan beklerken yeni bildirim rozete
  // ANINDA düşmez. Kullanıcı bunu bilerek seçti.
  useEffect(() => {
    if (!canRender) return undefined;

    fetchUnreadFast();

    const onVis = () => {
      if (!document.hidden) fetchUnreadFast();
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [canRender, fetchUnreadFast]);

  // Rota değişimi = tazeleme anı (zamanlayıcı yerine geçen doğal tetik).
  // İlk kurulum yukarıdaki effect'te zaten çekiyor → burada atlanır.
  const { pathname } = useLocation();
  const rotaRef = useRef(pathname);
  useEffect(() => {
    if (pathname === rotaRef.current) return;
    rotaRef.current = pathname;
    if (canRender) fetchUnreadFast();
  }, [pathname, canRender, fetchUnreadFast]);

  // esc / click-outside
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    const onDown = (e) => {
      const el = rootRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setOpen(false);
    };

    window.addEventListener("keydown", onKey, true);
    window.addEventListener("mousedown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("mousedown", onDown, true);
    };
  }, [open]);

  // dropdown açılınca listeyi bir kez çek (FAZ-65: artık poll yeniden başlamaz)
  useEffect(() => {
    if (!open) return;
    fetchDropdownList({ silent: false });
  }, [open, fetchDropdownList]);

  // ---- Navigation helpers ----------------------------------------------

  // FAZ-22 4b: canonical (birim localStorage/scope'tan; başkan/birim tek hedefe katlanır;
  // aktif birim seçili olmasa da bildirim tıklanabilir — slug bağımlılığı kalkar).
  const buildAllNotificationsPath = useCallback(() => "/yonetim/bildirimler", []);

  // ---- Actions ---------------------------------------------------------

  const handleItemClick = useCallback(
    (notif) => {
      if (isCancelledEvent(notif)) return;

      const nid = Number(notif?.id);

      // önce local (UX)
      if (nid) dispatch(markOneAsReadLocal(nid));

      // sonra backend
      if (nid) {
        dispatch(markRead({ ids: [nid] }))
          .unwrap?.()
          .then(() => log("markRead ok", nid))
          .catch((e) => log("markRead err", e));
      }

      // F1 "Olay → sohbet mesajı köprüsü" — TEK öncelikli yönlendirme:
      //   1) sohbet bildirimi (conversation_id) → sağ-alt panelde AÇ (köprü)
      //   2) görev bildirimi (task_id) → görev sohbeti (detay = gömülü sohbet)
      //   3) başvuru bildirimi (application_id) → başvuru detayı
      // FAZ-47: yönlendirme ortak çözücüde (zil/toaster/sayfa tek mantık).
      // Zil eskiden backend'in ürettiği target_url'i hiç okumuyordu.
      const target = resolveNotificationTarget(notif);
      if (!target) return;
      setOpen(false);
      if (target.kind === "chat") openChat(target.conversationId);
      else navigate(target.path);
    },
    [dispatch, navigate, log]
  );

  // FAZ-77 — pencereden "tümünü okundu". Eskiden okundu işaretlemenin TEK yolu
  // satırın kendisine tıklamaktı; ama tıklamak aynı zamanda o kayda GİDİYOR.
  // "Okudum, gitmek istemiyorum" diyen kullanıcının 15 satırı tek tek açıp geri
  // gelmesi gerekiyordu. Kapsam zilin kapsamıdır (mesajlar hariç değil —
  // backend'in `all` kapsamı; mesaj satırı da okundu olur ki alttaki mesaj
  // çizgisi de dürüst kalsın).
  const handleMarkAllRead = useCallback(() => {
    dispatch(markAllRead({}))
      .unwrap?.()
      .catch(() => {});
  }, [dispatch]);

  // Mesaj çizgisinin hedefi bildirim listesi DEĞİL, sohbet penceresinin
  // kendisidir — mesajın okunacağı yer orası (FAZ-51'den beri geçerli kural:
  // sohbet yüzeyi dock'ta yerinde açılır, rota değişmez).
  const goToMesajlar = useCallback(() => {
    setOpen(false);
    toggleChat();
  }, []);

  if (!canRender) return null;

  return (
    <div className="nb-notifs" ref={rootRef}>
      <button
        type="button"
        className="nb-notifs-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Bildirimler"
        title="Bildirimler"
      >
        <BellIcon className="nb-notifs-icon" />
        {/* FAZ-85 — SAYI YERİNE NOKTA.
            Kullanıcı (2026-08-01): "minik bir kırmızı nokta olsun, eğer bir
            tane bile okunmamış bildirim varsa — sayaç değil."
            Rozetteki sayı üç sorunu vardı: (a) kapsamı sidebar/sayfadakiyle
            aynı değildi (mesajlar hariçti) → aynı ekranda iki farklı sayı;
            (b) iki-üç hane olunca ikonu taşırıyordu; (c) "37 okunmamış" bilgisi
            navbar'da hiçbir karar değiştirmiyor. Nokta tek soruyu cevaplar:
            bakılacak bir şey var mı? Sayı, bakınca zaten pencerede yazıyor. */}
        {hasUnread ? (
          <span className="nb-notifs-dot" aria-hidden="true" />
        ) : null}
        <span className="nb-sr-only">
          {hasUnread ? "Okunmamış bildirim var" : "Okunmamış bildirim yok"}
        </span>
      </button>

      {open && (
        <div
          className="nb-notifs-dropdown pop-ok"
          data-ok="ust"
          data-ok-x="sag"
          role="dialog"
          aria-label="Bildirim listesi"
        >
          {/* FAZ-69 — BAŞLIK SADELEŞTİ.
              Eskiden burada beş şey vardı: "Tümü", "Ayarlar", Yenile, Kapat ve
              sayaç. Açılır pencere bir ÖNİZLEMEdir; yönetim işleri (ayarlar,
              tümünü okundu, filtre) merkezin kendi sayfasına ait.
                • Ayarlar KALKTI → sayfada (üstelik sayfanın üstüne modal açması
                  "saçma pencere" şikâyetinin kaynağıydı).
                • Yenile KALKTI → pencere zaten her açılışta çekiyor.
                • Kapat (✕) KALKTI → dışarı tıklama ve Esc zaten kapatıyor.
              Kalan tek çağrı altta: "Tüm bildirimler". */}
          {/* FAZ-77: başlık artık tek bir eylem taşıyor — "tümünü okundu".
              FAZ-69'da başlıktan beş şey kaldırılmıştı; okundu işaretleme de o
              temizlikte gitmişti ama onun yerine hiçbir şey konmamıştı, yani
              okumanın tek yolu satıra tıklayıp o kayda GİTMEK kalmıştı. */}
          <div className="nb-notifs-head">
            <div className="nb-notifs-title">
              Bildirimler{" "}
              {unreadCount > 0 ? (
                <span className="nb-notifs-title-muted">({unreadCount} okunmamış)</span>
              ) : (
                <span className="nb-notifs-title-muted">(hepsi okundu)</span>
              )}
            </div>
            {unreadCount > 0 ? (
              <button
                type="button"
                className="nb-notifs-headbtn"
                onClick={handleMarkAllRead}
                disabled={markingRead}
                title="Tümünü okundu işaretle"
              >
                <CheckIcon />
                <span>Okundu</span>
              </button>
            ) : null}
            {/* Liste doluyken yükleme yerleşimi OYNATMAZ: ince şerit. */}
            {!error && loading && dropdownItems.length > 0 ? (
              <span className="nb-notifs-progress" aria-hidden="true" />
            ) : null}
          </div>

          {error && (
            <div className="nb-notifs-alert nb-notifs-alert-error">
              <ExclamationTriangleIcon className="nb-notifs-alert-icon" />
              <div>{safeStr(error)}</div>
            </div>
          )}

          {!error && loading && dropdownItems.length === 0 ? (
            <SkeletonList />
          ) : (
            <NotificationsList
              items={dropdownItems}
              onItemClick={handleItemClick}
              markingRead={markingRead}
            />
          )}

          <div className="nb-notifs-foot">
            {/* FAZ-77 — MESAJ ÇİZGİSİ.
                Zil mesaj bildirimi taşımıyor (FAZ-60 kararı korunuyor) ama
                okunmamış mesajı hiç söylememek sinyali tamamen yok ediyordu:
                FAZ-51'de navbar'daki mesaj sayacı kaldırılıp sayı zile
                taşınmış, FAZ-60'ta zilden de çıkarılmıştı. Ayrı bir çizgi bu
                boşluğu kapatır: zil kirlenmez, iki sayı çelişmez, sinyal
                kaybolmaz. Hedefi bildirim listesi değil sohbet penceresidir. */}
            {mesajModulu && mesajUnread > 0 ? (
              <button
                type="button"
                className="nb-notifs-msgline"
                onClick={goToMesajlar}
                title="Sohbet penceresini aç"
              >
                <ChatBubbleLeftRightIcon />
                <span>
                  {mesajUnread > 99 ? "99+" : mesajUnread} okunmamış mesaj
                </span>
                <ChevronRightIcon className="nb-notifs-msgline-go" />
              </button>
            ) : null}

            {/* Tek ve net çıkış: merkeze git. */}
            <button
              type="button"
              className="nb-notifs-allbtn"
              onClick={() => {
                const p = buildAllNotificationsPath();
                setOpen(false);
                navigate(p);
              }}
            >
              <span>Tüm bildirimler</span>
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}